import { CreateUserFlowDraft, StepperStep, UserRoles } from '@features/users/interfaces';

/**
 * Dealer steps
 */
export const DEALER_STEPS: StepperStep[] = [
    {
        id: 'create-new-user',
        subtitle: 'Step 1',
        label: 'Create New User',
        description: 'You can fill on the forms manually or search a business',
    },
    {
        id: 'input-information',
        subtitle: 'Step 2',
        label: 'Input Information',
        description: 'Define screen layout and display configuration',
    },
    {
        id: 'preview-details',
        subtitle: 'Step 3',
        label: 'Preview Details',
        description: 'Verify all settings before finalizing the installation',
    },
];

/**
 * Sub Dealer steps
 */
export const SUB_DEALER_STEPS: StepperStep[] = [
    {
        id: 'create-new-user',
        subtitle: 'Step 1',
        label: 'Create New User',
        description: 'You can fill on the forms manually or search a business',
    },
    {
        id: 'input-information',
        subtitle: 'Step 2',
        label: 'Input Information',
        description: 'Define screen layout and display configuration',
    },
    {
        id: 'select-dealer',
        subtitle: 'Step 3',
        label: 'Select Dealer',
        description: 'Select a dealer to create a sub-dealer',
    },
    {
        id: 'preview-details',
        subtitle: 'Step 4',
        label: 'Preview Details',
        description: 'Verify all settings before finalizing the installation',
    },
];

/**
 * Default stepper steps when nothing is selected yet or used by any other user role
 */
export const DEFAULT_CREATE_USER_STEPS: StepperStep[] = [
    {
        id: 'create-new-user',
        subtitle: 'Step 1',
        label: 'Create New User',
        description: 'You can fill on the forms manually or search a business',
    },
    {
        id: 'input-information',
        subtitle: 'Step 2',
        label: 'Input Information',
        description: 'Define screen layout and display configuration',
    },
    {
        id: 'preview-details',
        subtitle: 'Step 3',
        label: 'Preview Details',
        description: 'Verify all settings before finalizing the installation',
    },
];

/**
 * Dealer Admin steps
 */
export const DEALER_ADMIN_STEPS: StepperStep[] = [
    {
        id: 'create-new-user',
        subtitle: 'Step 1',
        label: 'Create New User',
        description: 'You can fill on the forms manually or search a business',
    },
    {
        id: 'input-information',
        subtitle: 'Step 2',
        label: 'Input Information',
        description: 'Define screen layout and display configuration',
    },
    {
        id: 'select-dealer',
        subtitle: 'Step 3',
        label: 'Assign Dealer',
        description: 'Select a dealer to create a sub-dealer',
    },
    {
        id: 'preview-details',
        subtitle: 'Step 4',
        label: 'Preview Details',
        description: 'Verify all settings before finalizing the installation',
    },
];

/**
 * Create user steps based on selected user role/type
 *
 * Layout configuration for the sidebar stepper based on selected user role to be created
 */
export const CREATE_USER_STEPS: Record<UserRoles, StepperStep[]> = {
    none: DEFAULT_CREATE_USER_STEPS,
    dealer: DEALER_STEPS,
    subDealer: SUB_DEALER_STEPS,
    superAdmin: DEFAULT_CREATE_USER_STEPS,
    dealerAdmin: DEALER_ADMIN_STEPS,
    techOfficer: DEFAULT_CREATE_USER_STEPS,
    advertiser: DEFAULT_CREATE_USER_STEPS,
    guest: DEFAULT_CREATE_USER_STEPS,
};

/**
 * User roles
 */
export const USER_ROLES: { id: UserRoles; label: string; description: string; iconKey: string }[] =
    [
        {
            id: 'superAdmin',
            label: 'Admin',
            description: 'You can fill on the forms manually or search a business',
            iconKey: 'profile-filled',
        },
        {
            id: 'dealer',
            label: 'Dealer',
            description: 'You can fill on the forms manually or search a business',
            iconKey: 'dealers',
        },
    ];

export enum USER_ROLES_ENUM {
    SUPER_ADMIN = 'superAdmin',
    DEALER = 'dealer',
    SUB_DEALER = 'subDealer',
    DEALER_ADMIN = 'dealerAdmin',
    TECH_OFFICER = 'techOfficer',
    ADVERTISER = 'advertiser',
    GUEST = 'guest',
}

/**
 * A constant that contains an array all the steps a user role can perform
 *
 * @remarks
 * This constant is equivalent to the `CreateUserFlowDraft` interface. This allows us to extract the keys for a particular user role
 */
export const CreateUserFlowStepsByRole: Record<keyof CreateUserFlowDraft, string[]> = {
    dealer: ['input-information'],
    subDealer: ['input-information', 'select-dealer'],
    dealerAdmin: ['input-information', 'select-dealer'],
    techOfficer: ['input-information'],
    advertiser: ['input-information', 'select-dealer'],
    guest: ['input-information'],
    superAdmin: ['input-information'],
};
